import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApparatuurTab = ({ locatieId, locatieData, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Weet je zeker dat je deze apparatuur wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/locaties/${locatieId}/apparatuur/${itemId}`);
      toast.success('Apparatuur verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const apparatuur = locatieData?.apparatuur || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Apparatuur</h3>
          <p className="text-sm text-slate-500">{apparatuur.length} items</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Apparatuur toevoegen
        </Button>
      </div>

      {apparatuur.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <p className="text-slate-500">Geen apparatuur toegevoegd</p>
          <Button className="mt-4" onClick={handleAdd}>
            Eerste apparaat toevoegen
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Merk</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Serienummer</TableHead>
                <TableHead>Laatste Onderhoud</TableHead>
                <TableHead>Volgend Onderhoud</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apparatuur.map((item) => (
                <TableRow key={item._id}>
                  <TableCell className="font-medium">{item.naam}</TableCell>
                  <TableCell>{item.merk || '-'}</TableCell>
                  <TableCell>{item.type || '-'}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{item.serienummer || '-'}</TableCell>
                  <TableCell>{item.laatsteOnderhoud || '-'}</TableCell>
                  <TableCell>{item.volgendeOnderhoud || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item._id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ApparatuurModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        item={editingItem}
        locatieId={locatieId}
        onUpdate={onUpdate}
      />
    </div>
  );
};

const ApparatuurModal = ({ open, onClose, item, locatieId, onUpdate }) => {
  const [formData, setFormData] = useState({
    naam: '',
    merk: '',
    type: '',
    serienummer: '',
    aanschafDatum: '',
    laatsteOnderhoud: '',
    volgendeOnderhoud: '',
    handleidingUrl: '',
    notities: ''
  });

  React.useEffect(() => {
    if (item) {
      setFormData({
        naam: item.naam || '',
        merk: item.merk || '',
        type: item.type || '',
        serienummer: item.serienummer || '',
        aanschafDatum: item.aanschafDatum || '',
        laatsteOnderhoud: item.laatsteOnderhoud || '',
        volgendeOnderhoud: item.volgendeOnderhoud || '',
        handleidingUrl: item.handleidingUrl || '',
        notities: item.notities || ''
      });
    } else {
      setFormData({
        naam: '',
        merk: '',
        type: '',
        serienummer: '',
        aanschafDatum: '',
        laatsteOnderhoud: '',
        volgendeOnderhoud: '',
        handleidingUrl: '',
        notities: ''
      });
    }
  }, [item, open]);

  const handleSubmit = async () => {
    if (!formData.naam) {
      toast.error('Vul een naam in');
      return;
    }

    try {
      if (item) {
        await axios.put(`${API}/locaties/${locatieId}/apparatuur/${item._id}`, formData);
        toast.success('Apparatuur bijgewerkt');
      } else {
        await axios.post(`${API}/locaties/${locatieId}/apparatuur`, formData);
        toast.success('Apparatuur toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? 'Apparatuur bewerken' : 'Apparatuur toevoegen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Naam *</Label>
              <Input
                value={formData.naam}
                onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                placeholder="Portaalwasser"
              />
            </div>
            <div>
              <Label>Merk</Label>
              <Input
                value={formData.merk}
                onChange={(e) => setFormData({ ...formData, merk: e.target.value })}
                placeholder="WashTec"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Input
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                placeholder="SoftCare Pro"
              />
            </div>
            <div>
              <Label>Serienummer</Label>
              <Input
                value={formData.serienummer}
                onChange={(e) => setFormData({ ...formData, serienummer: e.target.value })}
                placeholder="WT-2023-001"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Aanschafdatum</Label>
              <Input
                type="date"
                value={formData.aanschafDatum}
                onChange={(e) => setFormData({ ...formData, aanschafDatum: e.target.value })}
              />
            </div>
            <div>
              <Label>Laatste onderhoud</Label>
              <Input
                type="date"
                value={formData.laatsteOnderhoud}
                onChange={(e) => setFormData({ ...formData, laatsteOnderhoud: e.target.value })}
              />
            </div>
            <div>
              <Label>Volgend onderhoud</Label>
              <Input
                type="date"
                value={formData.volgendeOnderhoud}
                onChange={(e) => setFormData({ ...formData, volgendeOnderhoud: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Handleiding URL</Label>
            <Input
              value={formData.handleidingUrl}
              onChange={(e) => setFormData({ ...formData, handleidingUrl: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={formData.notities}
              onChange={(e) => setFormData({ ...formData, notities: e.target.value })}
              placeholder="Extra informatie..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit}>{item ? 'Bijwerken' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApparatuurTab;
